import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Plus, Calendar, Users, IndianRupee, Edit, MapPin, Mountain } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HostDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [newProperty, setNewProperty] = useState({
    title: '',
    description: '',
    location: { country: 'India', region: '', city: '' },
    amenities: [],
    images: [''],
    price_per_night: 0,
    max_guests: 2,
    bedrooms: 1,
    bathrooms: 1,
    property_type: 'cabin'
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const userResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(userResponse.data);

      if (userResponse.data.role !== 'host') {
        await axios.post(`${BACKEND_URL}/api/users/switch-role?role=host`, {}, { withCredentials: true });
        userResponse.data.role = 'host';
      }

      const propertiesResponse = await axios.get(`${BACKEND_URL}/api/properties/host/${userResponse.data.user_id}`);
      setProperties(propertiesResponse.data);

      const bookingsResponse = await axios.get(`${BACKEND_URL}/api/bookings/host`, { withCredentials: true });
      setBookings(bookingsResponse.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to fetch data');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleAddProperty = async () => {
    if (!newProperty.title || !newProperty.description || !newProperty.location.city) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/properties`,
        {
          ...newProperty,
          images: newProperty.images.filter(img => img.trim() !== '') || ['https://images.pexels.com/photos/31790464/pexels-photo-31790464.jpeg'],
          amenities: newProperty.amenities.filter(a => a.trim() !== '')
        },
        { withCredentials: true }
      );

      toast.success('Property added successfully!');
      setShowAddProperty(false);
      fetchUserData();
      
      setNewProperty({
        title: '',
        description: '',
        location: { country: 'India', region: '', city: '' },
        amenities: [],
        images: [''],
        price_per_night: 0,
        max_guests: 2,
        bedrooms: 1,
        bathrooms: 1,
        property_type: 'cabin'
      });
    } catch (error) {
      toast.error('Failed to add property');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-spinner">
        <div className="w-16 h-16 border-4 border-[#8B2E2E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
              data-testid="nav-logo"
            >
              <Mountain className="h-8 w-8 text-[#8B2E2E]" />
              <h1 className="text-2xl font-bold text-[#8B2E2E]" style={{fontFamily: 'Playfair Display, serif'}}>GoHimalayan</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#4A4A4A]">Welcome, {user?.name}</span>
              <Button 
                variant="ghost"
                onClick={() => navigate('/traveler-dashboard')}
                data-testid="switch-to-traveler-btn"
              >
                Switch to Traveler
              </Button>
              <Button 
                variant="ghost"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 
            className="text-3xl font-bold text-[#4A4A4A]"
            style={{fontFamily: 'Playfair Display, serif'}}
            data-testid="dashboard-title"
          >
            Host Dashboard
          </h1>
          
          <Dialog open={showAddProperty} onOpenChange={setShowAddProperty}>
            <DialogTrigger asChild>
              <Button 
                className="rounded-full bg-[#8B2E2E] hover:bg-[#6B1E1E] text-white px-6"
                data-testid="add-property-btn"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-property-dialog">
              <DialogHeader>
                <DialogTitle style={{fontFamily: 'Playfair Display, serif'}}>Add New Property</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="title">Property Title *</Label>
                  <Input 
                    id="title"
                    value={newProperty.title}
                    onChange={(e) => setNewProperty({...newProperty, title: e.target.value})}
                    placeholder="Cozy Mountain Cabin in Manali"
                    data-testid="property-title-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea 
                    id="description"
                    value={newProperty.description}
                    onChange={(e) => setNewProperty({...newProperty, description: e.target.value})}
                    placeholder="Describe your property..."
                    rows={4}
                    data-testid="property-description-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input 
                      id="country"
                      value={newProperty.location.country}
                      onChange={(e) => setNewProperty({...newProperty, location: {...newProperty.location, country: e.target.value}})}
                      data-testid="property-country-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="region">Region</Label>
                    <Input 
                      id="region"
                      value={newProperty.location.region}
                      onChange={(e) => setNewProperty({...newProperty, location: {...newProperty.location, region: e.target.value}})}
                      placeholder="Himachal Pradesh"
                      data-testid="property-region-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input 
                      id="city"
                      value={newProperty.location.city}
                      onChange={(e) => setNewProperty({...newProperty, location: {...newProperty.location, city: e.target.value}})}
                      placeholder="Manali"
                      data-testid="property-city-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="price">Price/Night (₹) *</Label>
                    <Input 
                      id="price"
                      type="number"
                      value={newProperty.price_per_night}
                      onChange={(e) => setNewProperty({...newProperty, price_per_night: parseFloat(e.target.value)})}
                      data-testid="property-price-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="guests">Max Guests</Label>
                    <Input 
                      id="guests"
                      type="number"
                      value={newProperty.max_guests}
                      onChange={(e) => setNewProperty({...newProperty, max_guests: parseInt(e.target.value)})}
                      data-testid="property-guests-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bedrooms">Bedrooms</Label>
                    <Input 
                      id="bedrooms"
                      type="number"
                      value={newProperty.bedrooms}
                      onChange={(e) => setNewProperty({...newProperty, bedrooms: parseInt(e.target.value)})}
                      data-testid="property-bedrooms-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bathrooms">Bathrooms</Label>
                    <Input 
                      id="bathrooms"
                      type="number"
                      value={newProperty.bathrooms}
                      onChange={(e) => setNewProperty({...newProperty, bathrooms: parseInt(e.target.value)})}
                      data-testid="property-bathrooms-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="image">Image URL</Label>
                  <Input 
                    id="image"
                    value={newProperty.images[0]}
                    onChange={(e) => setNewProperty({...newProperty, images: [e.target.value]})}
                    placeholder="https://example.com/image.jpg"
                    data-testid="property-image-input"
                  />
                </div>

                <div>
                  <Label htmlFor="amenities">Amenities (comma separated)</Label>
                  <Input 
                    id="amenities"
                    value={newProperty.amenities.join(', ')}
                    onChange={(e) => setNewProperty({...newProperty, amenities: e.target.value.split(',').map(a => a.trim())})}
                    placeholder="WiFi, Parking, Heating, Breakfast"
                    data-testid="property-amenities-input"
                  />
                </div>

                <Button 
                  className="w-full rounded-full bg-[#8B2E2E] hover:bg-[#6B1E1E] text-white"
                  onClick={handleAddProperty}
                  data-testid="submit-property-btn"
                >
                  Add Property
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Properties Section */}
        <div className="mb-12" data-testid="properties-section">
          <h2 
            className="text-2xl font-bold mb-6 text-[#4A4A4A]"
            style={{fontFamily: 'Playfair Display, serif'}}
          >
            Your Properties ({properties.length})
          </h2>
          
          {properties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map((property, idx) => (
                <div 
                  key={property.property_id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/properties/${property.property_id}`)}
                  data-testid={`property-card-${idx}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={property.images[0] || 'https://images.pexels.com/photos/31790464/pexels-photo-31790464.jpeg'}
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold mb-2 text-[#4A4A4A]">{property.title}</h3>
                    <p className="text-sm text-[#4A4A4A]/70 mb-3">{property.location.city}, {property.location.country}</p>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center font-bold text-[#8B2E2E]">
                        <IndianRupee className="h-4 w-4" />
                        {property.price_per_night.toLocaleString()}/night
                      </span>
                      <Edit className="h-5 w-5 text-[#4A4A4A]/50" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-12 text-center" data-testid="no-properties">
              <Mountain className="h-16 w-16 text-[#4A4A4A]/30 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#4A4A4A] mb-2">No properties yet</h3>
              <p className="text-[#4A4A4A]/70 mb-6">Start by adding your first property</p>
            </div>
          )}
        </div>

        {/* Bookings Section */}
        <div data-testid="bookings-section">
          <h2 
            className="text-2xl font-bold mb-6 text-[#4A4A4A]"
            style={{fontFamily: 'Playfair Display, serif'}}
          >
            Recent Bookings ({bookings.length})
          </h2>
          
          {bookings.length > 0 ? (
            <div className="bg-white rounded-xl overflow-hidden">
              {bookings.slice(0, 5).map((booking, idx) => (
                <div 
                  key={booking.booking_id}
                  className="p-6 border-b border-gray-200 last:border-0 hover:bg-[#F5F5F7] transition-colors"
                  data-testid={`booking-${idx}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-[#4A4A4A] mb-1">Booking #{booking.booking_id.slice(-6).toUpperCase()}</h3>
                      <div className="flex gap-4 text-sm text-[#4A4A4A]/70">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {booking.check_in} - {booking.check_out}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {booking.guests} guests
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 font-bold text-[#4A4A4A] mb-1">
                        <IndianRupee className="h-4 w-4" />
                        {booking.total_amount.toLocaleString()}
                      </div>
                      <span className={`text-sm px-3 py-1 rounded-full ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl p-12 text-center" data-testid="no-bookings">
              <Calendar className="h-16 w-16 text-[#4A4A4A]/30 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#4A4A4A] mb-2">No bookings yet</h3>
              <p className="text-[#4A4A4A]/70">Your bookings will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HostDashboard;
